import type { ReactNode } from "react";
import type { Lang } from "../i18n.js";

/**
 * Flags for the language picker.
 *
 * Drawn rather than typed: the regional-indicator emoji (🇹🇷 / 🇬🇧) render as bare
 * letter pairs on Windows, which is exactly the platform an exporter's back
 * office runs on. These are simplified at 20x14 — recognisable at 14px, not
 * heraldically exact.
 */

const FLAGS: Record<Lang, { title: string; children: ReactNode }> = {
  en: {
    title: "English",
    children: (
      <>
        <rect width="20" height="14" fill="#012169" />
        <path d="M0 0 20 14M20 0 0 14" stroke="#ffffff" strokeWidth="2.8" />
        <path d="M0 0 20 14M20 0 0 14" stroke="#c8102e" strokeWidth="1.6" />
        <path d="M10 0V14M0 7H20" stroke="#ffffff" strokeWidth="4.6" />
        <path d="M10 0V14M0 7H20" stroke="#c8102e" strokeWidth="2.8" />
      </>
    ),
  },
  tr: {
    title: "Türkçe",
    children: (
      <>
        <rect width="20" height="14" fill="#e30a17" />
        {/* Crescent: a white disc with a red disc offset over it. */}
        <circle cx="8" cy="7" r="3.2" fill="#ffffff" />
        <circle cx="9.2" cy="7" r="2.6" fill="#e30a17" />
        <polygon
          points="12.7,5.25 13.11,6.43 14.36,6.46 13.37,7.22 13.73,8.42 12.7,7.7 11.67,8.42 12.03,7.22 11.04,6.46 12.29,6.43"
          fill="#ffffff"
        />
      </>
    ),
  },
};

export function LangFlag({ lang, size = 16 }: { lang: Lang; size?: number }) {
  const flag = FLAGS[lang];
  return (
    <svg
      width={size}
      height={(size / 20) * 14}
      viewBox="0 0 20 14"
      role="img"
      aria-label={flag.title}
      className="shrink-0 rounded-[2px] ring-1 ring-black/10"
    >
      {flag.children}
    </svg>
  );
}
